import React from 'react';
import { FiAtSign, FiCornerUpLeft, FiMessageCircle, FiSend, FiUserMinus, FiUserPlus } from 'react-icons/fi';
import type { ChannelActivityResponse } from '@troia-v3/types';

import { formatCompact, relativeSince } from './formatMetrics';

const asStr = (v: unknown): string => (typeof v === 'string' ? v : '');
const asNum = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

interface ActivityDescription {
  icon: React.ReactNode;
  label: string;
  text: string;
  tone: 'default' | 'up' | 'down';
}

/**
 * Descreve uma atividade para render. Lê os campos ACHATADOS de `data`
 * (Record<string,unknown>) defensivamente — nomes conforme os shapes tipados
 * de F5a (`ChannelActivity{Comment,Mention,FollowerDelta}Data` em @troia-v3/types):
 *   comment        → { mediaId, text, authorUsername }
 *   mention        → { mediaId, text, authorId, authorUsername?, commentId? } — autor achatado em `authorUsername` (fallback `authorId`)
 *   follower_delta → { date, delta, followersCount }
 *   story_reply/dm → sem fonte no backend ainda (F5a adiou) — mapeados defensivamente (ícone+label) para não quebrar em tipo inesperado
 */
function describeActivity(a: ChannelActivityResponse): ActivityDescription {
  const d = a.data;
  switch (a.activityType) {
    case 'comment': {
      const user = asStr(d.authorUsername);
      const text = asStr(d.text);
      return {
        icon: <FiMessageCircle size={15} />,
        label: 'Comentário',
        tone: 'default',
        text: user ? `@${user}: ${text}`.trim() : text || 'Novo comentário no seu post',
      };
    }
    case 'mention': {
      const user = asStr(d.authorUsername) || asStr(d.authorId);
      const text = asStr(d.text);
      return {
        icon: <FiAtSign size={15} />,
        label: 'Menção',
        tone: 'default',
        text: user ? `@${user} mencionou você${text ? `: ${text}` : ''}` : text || 'Você foi mencionado',
      };
    }
    case 'follower_delta': {
      const delta = asNum(d.delta);
      const total = asNum(d.followersCount);
      const up = delta >= 0;
      const totalTxt = total ? ` · ${formatCompact(total)} no total` : '';
      return {
        icon: up ? <FiUserPlus size={15} /> : <FiUserMinus size={15} />,
        label: 'Seguidores',
        tone: up ? 'up' : 'down',
        text: `${up ? '+' : ''}${delta} seguidores${totalTxt}`,
      };
    }
    case 'story_reply': {
      const text = asStr(d.text);
      return {
        icon: <FiCornerUpLeft size={15} />,
        label: 'Resposta ao story',
        tone: 'default',
        text: text || 'Respondeu ao seu story',
      };
    }
    case 'dm': {
      const text = asStr(d.text);
      return {
        icon: <FiSend size={15} />,
        label: 'Mensagem direta',
        tone: 'default',
        text: text || 'Nova mensagem direta',
      };
    }
    default:
      return { icon: <FiMessageCircle size={15} />, label: 'Atividade', tone: 'default', text: '' };
  }
}

export const ActivityRow: React.FC<{ activity: ChannelActivityResponse }> = ({ activity }) => {
  const { icon, label, text, tone } = describeActivity(activity);
  return (
    <div className="cdash-act-row" data-testid={`cdash-activity-${activity.id}`}>
      <div className="cdash-act-ic" data-tone={tone}>{icon}</div>
      <div className="cdash-act-body">
        <div className="cdash-act-head">
          <span className="cdash-act-label">{label}</span>
          <span className="cdash-act-time">{relativeSince(activity.occurredAt)}</span>
        </div>
        {text ? <div className="cdash-act-text">{text}</div> : null}
      </div>
    </div>
  );
};
